// src/components/ui/Icon.tsx — stroke-based, brass-tuned icon set.
// Verbatim port of docs/prototype/icons.jsx. Each named icon is a thin
// wrapper around the base <Icon> with the SVG path data baked in.

import type { CSSProperties } from "react";

export interface IconProps {
  s?: number;
  c?: string;
  sw?: number;
  fill?: boolean;
  style?: CSSProperties;
}

interface BaseIconProps extends IconProps {
  d: string | string[];
}

const BaseIcon = ({ d, s = 22, c = "currentColor", sw = 1.7, fill = false, style }: BaseIconProps) => (
  <svg
    width={s}
    height={s}
    viewBox="0 0 24 24"
    fill={fill ? c : "none"}
    stroke={fill ? "none" : c}
    strokeWidth={sw}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ display: "block", flexShrink: 0, ...style }}
  >
    {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
  </svg>
);

const make = (d: string | string[]) => (props: IconProps) => <BaseIcon {...props} d={d} />;

export const Home      = make("M3 11.5 12 4l9 7.5V20a1 1 0 0 1-1 1h-4v-6h-8v6H4a1 1 0 0 1-1-1z");
export const Grid      = make(["M4 4h6v6H4z", "M14 4h6v6h-6z", "M4 14h6v6H4z", "M14 14h6v6h-6z"]);
export const Doc       = make(["M6 3h8l4 4v14H6z", "M14 3v4h4", "M9 12h6", "M9 16h4"]);
export const User      = make(["M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8z", "M4 21c1.5-4 4.5-6 8-6s6.5 2 8 6"]);
export const Bell      = make(["M6 17V11a6 6 0 0 1 12 0v6", "M4 17h16", "M10 20a2 2 0 0 0 4 0"]);
export const Train     = make([
  "M6 4h12a2 2 0 0 1 2 2v9a3 3 0 0 1-3 3H7a3 3 0 0 1-3-3V6a2 2 0 0 1 2-2z",
  "M4 11h16",
  "M8 21l-2-3",
  "M16 21l2-3",
  "M8.5 14.5h.01",
  "M15.5 14.5h.01",
]);
export const Phone     = make("M5 4h3l2 5-2 1a11 11 0 0 0 6 6l1-2 5 2v3a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2z");
export const Mail      = make(["M3 6h18v12H3z", "m3 6 9 7 9-7"]);
export const Search    = make(["M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16z", "m21 21-4.3-4.3"]);
export const Back      = make(["M19 12H5", "m12 5-7 7 7 7"]);
export const Forward   = make(["M5 12h14", "m12 5 7 7-7 7"]);
export const Cal       = make(["M4 6h16v15H4z", "M4 10h16", "M9 3v4", "M15 3v4"]);
export const Ticket    = make([
  "M3 9a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v2a2 2 0 0 0 0 4v2a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-2a2 2 0 0 0 0-4z",
  "M13 7v10",
  "M13 10.5v.01",
  "M13 13.5v.01",
]);
// Driving Licence Check — an ID/driving-licence card (photo box + two text lines).
export const Licence   = make([
  "M3 6h18a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1z",
  "M6 10.5h4.5v4H6z",
  "M13 10.5h5",
  "M13 14h5",
]);
export const Sun       = make([
  "M12 5V3", "M12 21v-2", "M5 12H3", "M21 12h-2",
  "m5.6 5.6 1.4 1.4", "m17 17 1.4 1.4", "m5.6 18.4 1.4-1.4", "m17 7 1.4-1.4",
  "M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8z",
]);
export const Cloud     = make("M7 18a4 4 0 1 1 .8-7.9 5.5 5.5 0 0 1 10.7 1.4A3.5 3.5 0 0 1 17.5 18z");
export const Drizzle   = make([
  "M7 14a4 4 0 1 1 .8-7.9 5.5 5.5 0 0 1 10.7 1.4A3.5 3.5 0 0 1 17.5 14H7",
  "M9 18v2", "M13 17v3", "M17 18v2",
]);
export const Alert     = make(["m12 3 10 18H2z", "M12 10v5", "M12 18v.01"]);
export const Briefcase = make(["M4 8h16v12H4z", "M9 8V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v3", "M4 13h16"]);
export const News      = make(["M5 4h14v16H5z", "M9 8h6", "M9 12h6", "M9 16h4"]);
export const Edit      = make(["M4 20h4l11-11-4-4L4 16z", "M14 5l4 4"]);
export const Users     = make([
  "M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z",
  "M2 20c1-3.5 3.5-5.5 7-5.5s6 2 7 5.5",
  "M17 11a3 3 0 1 0 0-6",
  "M16 20c.5-2.5 2-4 4-4.5",
]);
export const Plus      = make(["M12 5v14", "M5 12h14"]);
export const Check     = make("m5 12 4 4L19 6");
export const Close     = make(["M6 6l12 12", "M18 6 6 18"]);
export const Chev      = make("m9 6 6 6-6 6");
export const Down      = make("m6 9 6 6 6-6");
export const Up        = make("m6 15 6-6 6 6");
export const Pin       = make([
  "M12 22s7-7.5 7-13a7 7 0 0 0-14 0c0 5.5 7 13 7 13z",
  "M12 11.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z",
]);
export const Settings  = make([
  "M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z",
  "M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1A2 2 0 1 1 4.4 17l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 0 1 0-4h.1a1.7 1.7 0 0 0 1.5-1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z",
]);
export const Logout    = make(["M9 4H5a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h4", "M16 17l5-5-5-5", "M21 12H9"]);
export const Star      = make("m12 3 2.6 6 6.4.6-4.8 4.4 1.4 6.4L12 17l-5.6 3.4 1.4-6.4L3 9.6 9.4 9z");
export const Folder    = make("M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z");
export const Map       = make(["M9 3 3 5v16l6-2 6 2 6-2V3l-6 2z", "M9 3v16", "M15 5v16"]);
export const Wrench    = make("M14 4a5 5 0 0 1 5 7l3 3-3 3-3-3a5 5 0 0 1-7-5l2 2 3-3z");
export const Heart     = make("M12 21s-7-4.5-7-10a4 4 0 0 1 7-3 4 4 0 0 1 7 3c0 5.5-7 10-7 10z");
export const Lightning = make("m13 2-8 12h6l-2 8 8-12h-6z");
export const Filter    = make("M4 4h16l-6 8v6l-4 2v-8z");
export const Eye       = make([
  "M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z",
  "M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z",
]);
export const Compass   = make(["M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z", "m16 8-2 6-6 2 2-6z"]);
// Reports applet — a small bar chart (matches the prototype's measure glyph).
export const Bars      = make(["M4 20V10", "M10 20V4", "M16 20v-7", "M21 20H3"]);
// CamView applet — a CCTV/camera glyph.
export const Camera    = make(["M3 8a2 2 0 0 1 2-2h2l1.5-2h7L19 6h0a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z", "M12 16a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z"]);

// Info Hub Scheduler additions — verbatim paths from the scheduler prototype's
// icons.jsx, kept here so both the app tile and the /scheduler pages resolve
// them from the one shared icon set.
export const Clock     = make(["M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z", "M12 7v5l3 2"]);
export const Repeat    = make(["M17 2l4 4-4 4", "M3 11V9a4 4 0 0 1 4-4h14", "M7 22l-4-4 4-4", "M21 13v2a4 4 0 0 1-4 4H3"]);
export const Sliders   = make(["M4 6h10", "M18 6h2", "M4 12h4", "M12 12h8", "M4 18h12", "M18 18h2", "M14 4v4", "M8 10v4", "M16 16v4"]);
export const History   = make(["M3 12a9 9 0 1 0 3-6.7L3 8", "M3 4v4h4", "M12 8v4l3 2"]);
export const Hash      = make(["M9 4 7 20", "M17 4l-2 16", "M5 9h15", "M4 15h15"]);
export const Target    = make(["M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z", "M12 17a5 5 0 1 0 0-10 5 5 0 0 0 0 10z", "M12 13.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z"]);
export const Info      = make(["M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z", "M12 11v5", "M12 8v.01"]);
export const Database  = make(["M12 8c4.4 0 8-1.3 8-3s-3.6-3-8-3-8 1.3-8 3 3.6 3 8 3z", "M4 5v6c0 1.7 3.6 3 8 3s8-1.3 8-3V5", "M4 11v6c0 1.7 3.6 3 8 3s8-1.3 8-3v-6"]);
export const Play      = (props: IconProps) => <BaseIcon {...props} d="M7 4v16l13-8z" fill />;
export const Code      = make(["m8 8-4 4 4 4", "m16 8 4 4-4 4", "M13 6l-2 12"]);

// Helper: utility to render a small inline glyph in non-React contexts
// (e.g. inside other primitives that need a back-arrow). Use the named
// exports above in normal page code.
export const _BaseIcon = BaseIcon;
